import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import debounce from 'lodash/debounce';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import { ADDRESS_PREDICTION_PROVIDER_SMARTY } from 'common/utility/constants';
import { SR_API_ACTION_ADDRESS_GET_AUTOCOMPLETE_PREDICTIONS } from 'api/utilityActionTypes';
import emptyFunction from 'common/util/emptyFunction';
import './SrFormFieldSmartyAddressAutoComplete.less';

const SuggestionItem = ({ description, entriesCount }) => {
  return (
    <div>
      <div styleName="suggestionDescription">{description}</div>
      {entriesCount > 1 && (
        <div styleName="suggestionMetadata">Requires Unit • {entriesCount} Options</div>
      )}
    </div>
  );
};

const SrFormFieldSmartyAddressAutoComplete = ({
  value,
  formField,
  onChange,
  textInputFieldAttributes = {},
  onBlur,
  onSelectAddress,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isRefiningSearch, setIsRefiningSearch] = useState(false);

  const dispatch = useDispatch();

  // === API Call Functions ===

  const fetchAddressPredictions = useCallback(
    (searchText, selectedAddress = null) => {
      if (searchText?.trim().length <= 3) {
        setSuggestions([]);
        return;
      }

      return dispatch(requestUtilityApi, {
        queryId: `${formField.id}_${searchText}`,
        action: SR_API_ACTION_ADDRESS_GET_AUTOCOMPLETE_PREDICTIONS,
        payload: {
          search: searchText,
          ...(selectedAddress && { selected: selectedAddress }),
          addressSearchProvider: ADDRESS_PREDICTION_PROVIDER_SMARTY,
        },
      }).then((response) => {
        setSuggestions(response?.predictions || []);
        return response?.predictions || [];
      });
    },
    [dispatch, formField.id],
  );

  const debouncedFetchPredictions = useMemo(() => debounce(fetchAddressPredictions, 300), [
    fetchAddressPredictions,
  ]);

  /**
   * Builds a query for addresses that require secondary selection (multi-unit buildings).
   * Returns the search string and full selected address for API filtering.
   */
  const buildSecondarySearchQuery = (suggestion) => {
    const { streetLine, secondary, entriesCount, city, state, zipcode } = suggestion;
    const searchString = `${streetLine} ${secondary}`;
    return {
      search: searchString,
      selected: `${searchString} (${entriesCount}) ${city} ${state} ${zipcode}`,
    };
  };

  // === Event Handlers ===

  const handleInputChange = (event, { newValue }) => {
    // During secondary search refinement, don't update the parent value yet
    if (!isRefiningSearch) {
      setSelectedAddress(null);
      onChange(newValue);
    }

    // Fetch new predictions as user types
    debouncedFetchPredictions(newValue);

    // Clear any previously selected address
    if (onSelectAddress) {
      onSelectAddress(null);
    }
  };

  const handleAddressRefinement = (suggestion) => {
    // Multi-unit building: fetch refined results with unit numbers
    debouncedFetchPredictions.cancel();
    setIsRefiningSearch(true);

    const { search, selected } = buildSecondarySearchQuery(suggestion);
    onChange(search);
    fetchAddressPredictions(search, selected).then(() => {
      setIsRefiningSearch(false);
    });
  };

  const handleSingleAddressSelection = (suggestion) => {
    // Single address: update value and notify parent
    onChange(suggestion.description);

    // Notify select address handler if provided
    if (onSelectAddress) {
      onSelectAddress(suggestion.description);
      setSelectedAddress(suggestion.description);
    }
    // Notify blur handler if provided
    if (onBlur) {
      onBlur(suggestion.description);
    }
  };

  const handleSuggestionSelection = (event, { suggestion }) => {
    const isMultiUnit = suggestion.entriesCount > 1;

    if (isMultiUnit) {
      handleAddressRefinement(suggestion);
    } else {
      handleSingleAddressSelection(suggestion);
    }
    debouncedFetchPredictions.cancel();
    setSuggestions([]);
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur(selectedAddress);
    }
  };

  const handleKeyDownCapture = (event) => {
    // Disable keyboard navigation: prevent arrow keys and Enter from navigating/selecting suggestions
    // Uses capture phase to intercept events before react-autosuggest processes them
    if (
      suggestions.length > 0 &&
      (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter')
    ) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  // === Render ===

  const shouldShowSuggestions = suggestions.length > 0;

  return (
    <div onKeyDownCapture={handleKeyDownCapture}>
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsClearRequested={emptyFunction}
        onSuggestionsFetchRequested={emptyFunction}
        getSuggestionValue={(suggestion) => suggestion.description}
        renderSuggestion={(suggestion) => (
          <SuggestionItem
            description={suggestion.description}
            entriesCount={suggestion.entriesCount}
          />
        )}
        shouldRenderSuggestions={() => shouldShowSuggestions}
        onSuggestionSelected={handleSuggestionSelection}
        highlightFirstSuggestion={false}
        inputProps={{
          ...textInputFieldAttributes,
          autoComplete: 'off',
          'data-form-type': 'other',
          'data-1p-ignore': true,
          'data-lpignore': true,
          'data-bwignore': true,
          value: value || '',
          onChange: handleInputChange,
          onBlur: handleBlur,
        }}
      />
    </div>
  );
};

SrFormFieldSmartyAddressAutoComplete.propTypes = {
  value: PropTypes.string,
  formField: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  textInputFieldAttributes: PropTypes.object,
  onBlur: PropTypes.func,
  onSelectAddress: PropTypes.func,
};

export default SrFormFieldSmartyAddressAutoComplete;
